import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useScheduleSession } from '@/features/murajaah/hooks';
import type { MurajaahSessionType } from '@/features/murajaah/types';
import { ApiError } from '@/lib/api/client';

/**
 * `POST /memorization/murajaah` (`scheduleSessionSchema`, Phase G-2, Job
 * 16/17). `teacherId` is never sent - no teacher-search/name-resolution UI
 * exists anywhere in this app (the same limitation established in Phase
 * G-1), so the acting Teacher/Admin's own authority is what the backend
 * checks; the row is left unattributed when an Org Admin schedules it,
 * matching the same pattern Target/Submission creation already use.
 */
export function ScheduleSessionDialog({
  studentId,
  open,
  onOpenChange,
}: {
  readonly studentId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [scheduledAt, setScheduledAt] = useState('');
  const [sessionType, setSessionType] = useState<MurajaahSessionType>('SCHEDULED');
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const schedule = useScheduleSession(studentId);

  function reset(): void {
    setScheduledAt('');
    setSessionType('SCHEDULED');
    setNotes('');
    setValidationError(null);
    schedule.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (scheduledAt === '') {
      setValidationError('Pilih tanggal dan waktu murajaah.');
      return;
    }
    const scheduledDate = new Date(scheduledAt);
    if (Number.isNaN(scheduledDate.getTime())) {
      setValidationError('Tanggal dan waktu tidak valid.');
      return;
    }

    schedule.mutate(
      {
        studentId,
        scheduledAt: scheduledDate.toISOString(),
        sessionType,
        notes: notes.trim().length > 0 ? notes.trim() : null,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    schedule.error instanceof ApiError
      ? schedule.error.message
      : schedule.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Jadwalkan Murajaah</DialogTitle>
          <DialogDescription>Tentukan waktu dan jenis sesi murajaah.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="murajaah-scheduled-at">Tanggal dan Waktu</Label>
            <Input
              id="murajaah-scheduled-at"
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => {
                setScheduledAt(event.target.value);
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="murajaah-type">Jenis Sesi</Label>
            <Select
              value={sessionType}
              onValueChange={(value) => {
                setSessionType(value as MurajaahSessionType);
              }}
            >
              <SelectTrigger id="murajaah-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SCHEDULED">Terjadwal</SelectItem>
                <SelectItem value="SPONTANEOUS">Spontan</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="murajaah-notes">Catatan (opsional)</Label>
            <Textarea
              id="murajaah-notes"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={schedule.isPending}>
              {schedule.isPending ? 'Menyimpan...' : 'Jadwalkan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
